import React, { useState, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useCompany } from '../contexts/CompanyContext';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useToast } from '../../shared/contexts/ToastContext';
import { Company } from '../types/Company';

interface WebhookResponse {
  company_id: number;
  company_added: boolean;
  glossary_added: boolean;
  normalize_added: boolean;
}

interface AddCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    glossary: File | null;
    normalize: File | null;
  }) => Promise<Company | null>;
}

const AddCompanyModal: React.FC<AddCompanyModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [companyName, setCompanyName] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [glossaryFile, setGlossaryFile] = useState<File | null>(null);
  /* NORMALIZE FIELD - Uncomment to enable
  const [normalizeFile, setNormalizeFile] = useState<File | null>(null);
  */
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSimilarityWarning, setShowSimilarityWarning] = useState(false);
  const [similarCompanies, setSimilarCompanies] = useState<Array<{company: any, similarity: number}>>([]);
  const [errors, setErrors] = useState<{
    name?: string;
    code?: string;
    glossary?: string;
    /* NORMALIZE FIELD - Uncomment to enable
    normalize?: string;
    */
  }>({});
  const [webhookResult, setWebhookResult] = useState<WebhookResponse | null>(null);
  const [showResult, setShowResult] = useState(false);

  const { checkSimilarity, refreshCompanies } = useCompany();
  const { user } = useAuth();
  const { success, error, warning } = useToast();

  const glossaryInputRef = useRef<HTMLInputElement>(null);
  /* NORMALIZE FIELD - Uncomment to enable
  const normalizeInputRef = useRef<HTMLInputElement>(null);
  */

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!companyName.trim()) {
      newErrors.name = 'Şirket adı gereklidir';
    }

    // File validation only if files are provided (optional now)
    if (glossaryFile && !glossaryFile.name.toLowerCase().endsWith('.xlsx') && !glossaryFile.name.toLowerCase().endsWith('.xls')) {
      newErrors.glossary = 'Sadece Excel dosyaları (.xlsx, .xls) kabul edilir';
    }

    /* NORMALIZE FIELD - Uncomment to enable
    if (normalizeFile && !normalizeFile.name.toLowerCase().endsWith('.xlsx') && !normalizeFile.name.toLowerCase().endsWith('.xls')) {
      newErrors.normalize = 'Sadece Excel dosyaları (.xlsx, .xls) kabul edilir';
    }
    */

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check for similar companies when name changes
  useEffect(() => {
    if (companyName.trim().length > 2) {
      const similarities = checkSimilarity(companyName.trim());
      const highSimilarities = similarities.filter((s: any) => s.similarity >= 50);
      
      if (highSimilarities.length > 0) {
        setSimilarCompanies(highSimilarities);
        setShowSimilarityWarning(true);
      } else {
        setShowSimilarityWarning(false);
        setSimilarCompanies([]);
      }
    } else {
      setShowSimilarityWarning(false);
      setSimilarCompanies([]);
    }
  }, [companyName, checkSimilarity]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // If there are similar companies, show confirmation
    if (showSimilarityWarning && similarCompanies.length > 0) {
      // Don't proceed, let user decide via the warning UI
      return;
    }

    await submitCompany();
  };

  const submitCompany = async () => {
    setIsSubmitting(true);
    try {
      // Webhook'a şirket bilgilerini gönder
      const formData = new FormData();
      formData.append('company_name', companyName.trim());
      formData.append('company_code', companyCode.trim());
      formData.append('user_id', user?.id || 'unknown');
      formData.append('create_date', new Date().toISOString().replace('T', ' ').replace('Z', ''));
      
      // UUID'ler oluştur
      const glossaryId = uuidv4();
      const normalizeId = uuidv4();
      
      // UUID'leri ekle
      formData.append('glossary_id', glossaryId);
      /* NORMALIZE FIELD - Uncomment to enable
      formData.append('normalize_id', normalizeId);
      */
      
      // Dosyaları ekle veya null gönder
      if (glossaryFile) {
        formData.append('glossary', glossaryFile);
      } else {
        formData.append('glossary', 'null');
      }
      
      /* NORMALIZE FIELD - Uncomment to enable
      if (normalizeFile) {
        formData.append('normalize', normalizeFile);
      } else {
        formData.append('normalize', 'null');
      }
      */
      // Normalize alanı kapalı olduğunda her zaman null gönder
      formData.append('normalize', 'null');

      // Debug: Gönderilen verileri logla
      console.log('Webhook\'a gönderilen veriler:', {
        company_name: companyName.trim(),
        company_code: companyCode.trim(),
        user_id: user?.id || 'unknown',
        create_date: new Date().toISOString().replace('T', ' ').replace('Z', ''),
        glossary_id: glossaryId,
        /* NORMALIZE FIELD - Uncomment to enable
        normalize_id: normalizeId,
        */
        glossary: glossaryFile ? glossaryFile.name : 'null',
        /* NORMALIZE FIELD - Uncomment to enable
        normalize: normalizeFile ? normalizeFile.name : 'null'
        */
      });

      // Webhook'a veri göndermeyi dene
      let webhookSuccess = false;
      let webhookData: WebhookResponse | null = null;
      
      try {
        const webhookResponse = await fetch('https://n8n.mahir.ai/webhook/add_company', {
          method: 'POST',
          body: formData
        });

        if (webhookResponse.ok) {
          webhookData = await webhookResponse.json() as WebhookResponse;
          webhookSuccess = true;
          console.log('Webhook başarıyla çağrıldı:', webhookData);
        } else {
          console.warn(`Webhook uyarısı: ${webhookResponse.status} - ${await webhookResponse.text()}`);
          // Hata durumunda varsayılan değerler
          webhookData = {
            company_id: 0,
            company_added: false,
            glossary_added: false,
            normalize_added: false
          };
        }
      } catch (webhookError) {
        console.warn('Webhook çağrısında hata oluştu:', webhookError);
        // Hata durumunda varsayılan değerler
        webhookData = {
          company_id: 0,
          company_added: false,
          glossary_added: false,
          normalize_added: false
        };
      }

      // Webhook sonucunu göster
      if (webhookData) {
        setWebhookResult(webhookData);
        setShowResult(true);
        setIsSubmitting(false);
        return; // Burada duruyoruz, kullanıcı sonuçları görecek
      }
      
      // Webhook başarısız olsa da local state'i güncelle
      const newCompany = await onSubmit({
        name: companyName.trim(),
        glossary: glossaryFile,
        /* NORMALIZE FIELD - Uncomment to enable
        normalize: normalizeFile
        */
        normalize: null // Normalize alanı kapalı
      });

      if (newCompany) {
        handleClose();
      }
    } catch (error) {
      console.error('Şirket eklenirken hata:', error);
      // Hata durumunda kullanıcıya bilgi ver
      setErrors({ ...errors, name: 'Şirket eklenirken bir hata oluştu. Lütfen tekrar deneyin.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceedAnyway = () => {
    setShowSimilarityWarning(false);
    setSimilarCompanies([]);
    submitCompany();
  };

  const handleClose = () => {
    setCompanyName('');
    setCompanyCode('');
    setGlossaryFile(null);
    /* NORMALIZE FIELD - Uncomment to enable
    setNormalizeFile(null);
    */
    setErrors({});
    setIsSubmitting(false);
    setShowSimilarityWarning(false);
    setSimilarCompanies([]);
    setWebhookResult(null);
    setShowResult(false);
    if (glossaryInputRef.current) glossaryInputRef.current.value = '';
    /* NORMALIZE FIELD - Uncomment to enable
    if (normalizeInputRef.current) normalizeInputRef.current.value = '';
    */
    onClose();
  };

  const handleFileChange = (type: 'glossary' /* NORMALIZE FIELD: | 'normalize' */, file: File | null) => {
    if (type === 'glossary') {
      setGlossaryFile(file);
      setErrors(prev => ({ ...prev, glossary: undefined }));
    }
    /* NORMALIZE FIELD - Uncomment to enable
    else {
      setNormalizeFile(file);
      setErrors(prev => ({ ...prev, normalize: undefined }));
    }
    */
  };

  // Sonuç görselleştirme bileşeni
  const ResultVisualization = () => {
    if (!webhookResult) return null;

    const steps = [
      {
        key: 'company_added',
        label: 'Şirket Ekleme',
        success: webhookResult.company_added,
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
          </svg>
        )
      },
      {
        key: 'glossary_added',
        label: 'Glossary Ekleme',
        success: webhookResult.glossary_added,
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
          </svg>
        )
      },
      /* NORMALIZE FIELD - Uncomment to enable
      {
        key: 'normalize_added',
        label: 'Normalize Ekleme',
        success: webhookResult.normalize_added,
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
        )
      }
      */
    ];

    const allSuccess = steps.every(step => step.success);

    return (
      <div className="space-y-4">
        {/* Genel Durum */}
        <div className="p-4 rounded-lg border-2 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
          <div className="flex items-center">
            <div className="mr-3 text-blue-700 dark:text-white">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-lg text-blue-800 dark:text-white">
                Yükleme İşlemi Tamamlandı
              </h3>
              <p className="text-sm text-blue-700 dark:text-white">
                Aşağıda hangi verilerin yüklendiğini görebilirsiniz.
              </p>
            </div>
          </div>
        </div>

        {/* Adım Detayları */}
        <div className="space-y-3">
          {steps.map((step, index) => (
            <div 
              key={step.key}
              className={`flex items-center p-3 rounded-lg border transition-all duration-300 ${
                step.success
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
              }`}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <div className={`mr-3 animate-fade-in ${
                step.success 
                  ? 'text-green-700 dark:text-green-400' 
                  : 'text-red-700 dark:text-red-400'
              }`}>
                {step.icon}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className={`font-medium ${
                    step.success 
                      ? 'text-green-800 dark:text-green-200' 
                      : 'text-red-800 dark:text-red-200'
                  }`}>
                    {step.label}
                  </span>
                  <div className={`flex items-center ${
                    step.success ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                  }`}>
                    {step.success ? (
                      <svg className="w-5 h-5 animate-scale-in" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 animate-shake" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
                <p className={`text-xs mt-1 ${
                  step.success 
                    ? 'text-green-700 dark:text-green-300' 
                    : 'text-red-700 dark:text-red-300'
                }`}>
                  {step.success ? 'Yüklendi' : 'Yüklenmedi'}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Aksiyon Butonu */}
        <div className="pt-4">
          <button
            onClick={async () => {
              // Webhook'tan company_added: true dönmüşse şirketi local state'e ekle
              if (webhookResult?.company_added && webhookResult?.company_id) {
                const newCompany = await onSubmit({
                  name: companyName.trim(),
                  glossary: glossaryFile,
                  /* NORMALIZE FIELD - Uncomment to enable
                  normalize: normalizeFile
                  */
                  normalize: null // Normalize alanı kapalı
                });
                
                if (newCompany) {
                  // Webhook'tan gelen company_id'yi newCompany'ye ekle
                  newCompany.company_id = webhookResult.company_id;
                  
                  // Şirket eklendikten sonra webhook'tan güncel veriyi çek
                  await refreshCompanies();
                  handleClose();
                }
              } else {
                // Şirket eklenmemişse sadece modal'ı kapat
                handleClose();
              }
            }}
            className="w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 bg-blue-500 hover:bg-blue-600 text-white"
          >
            Tamam
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {showResult ? 'İşlem Sonucu' : 'Yeni Şirket Ekle'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* İçerik */}
        {showResult ? (
          <ResultVisualization />
        ) : (
          <>
            
            <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Şirket Adı *
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => {
                if (e.target.value.length <= 50) {
                  setCompanyName(e.target.value);
                  setErrors(prev => ({ ...prev, name: undefined }));
                }
              }}
              placeholder="Şirket adını girin"
              maxLength={50}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#02c2ff] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors ${
                errors.name 
                  ? 'border-red-500 dark:border-red-500' 
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            <div className="flex justify-between items-center mt-1">
              <div></div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {companyName.length}/50
              </span>
            </div>
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
            
            {/* Similarity Warning */}
            {showSimilarityWarning && similarCompanies.length > 0 && (
              <div className="mt-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Benzer şirket adları bulundu
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                      <p className="mb-2">Aşağıdaki şirketlerle benzerlik tespit edildi:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {similarCompanies.map((item, index) => (
                          <li key={index} className="flex items-center justify-between">
                            <span>{item.company.name}</span>
                            <span className="text-xs bg-yellow-200 dark:bg-yellow-800 px-2 py-1 rounded-full">
                              %{Math.round(item.similarity)} benzerlik
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-4 flex space-x-3">
                      <button
                        type="button"
                        onClick={handleProceedAnyway}
                        disabled={isSubmitting}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                      >
                        Yine de Ekle
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowSimilarityWarning(false);
                          setSimilarCompanies([]);
                        }}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        İptal Et
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Company Code */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Şirket Kodu
            </label>
            <input
              type="text"
              value={companyCode}
              onChange={(e) => {
                if (e.target.value.length <= 50) {
                  setCompanyCode(e.target.value);
                  setErrors(prev => ({ ...prev, code: undefined }));
                }
              }}
              placeholder="Şirket kodunu girin"
              maxLength={50}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#02c2ff] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors ${
                errors.code 
                  ? 'border-red-500 dark:border-red-500' 
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            <div className="flex justify-between items-center mt-1">
              <div></div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {companyCode.length}/50
              </span>
            </div>
            {errors.code && (
              <p className="mt-1 text-sm text-red-500">{errors.code}</p>
            )}
          </div>

          {/* Glossary File */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Glossary Dosyası (Excel)
            </label>
            <div className="relative">
              <input
                ref={glossaryInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => handleFileChange('glossary', e.target.files?.[0] || null)}
                className="hidden"
                id="glossary-upload"
              />
              <label
                htmlFor="glossary-upload"
                className={`flex items-center justify-center w-full px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  errors.glossary
                    ? 'border-red-500 dark:border-red-500'
                    : glossaryFile
                    ? 'border-green-500 dark:border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <div className="text-center">
                  <svg className={`mx-auto h-8 w-8 mb-2 ${
                    glossaryFile ? 'text-green-500' : 'text-gray-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {glossaryFile ? glossaryFile.name : 'Glossary dosyasını seçin (opsiyonel)'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Excel formatında (.xlsx, .xls)
                  </p>
                </div>
              </label>
            </div>
            {errors.glossary && (
              <p className="mt-1 text-sm text-red-500">{errors.glossary}</p>
            )}
          </div>

          {/* NORMALIZE FIELD - Uncomment to enable
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Normalize Dosyası (Excel)
            </label>
            <div className="relative">
              <input
                ref={normalizeInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => handleFileChange('normalize', e.target.files?.[0] || null)}
                className="hidden"
                id="normalize-upload"
              />
              <label
                htmlFor="normalize-upload"
                className={`flex items-center justify-center w-full px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  errors.normalize
                    ? 'border-red-500 dark:border-red-500'
                    : normalizeFile
                    ? 'border-green-500 dark:border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <div className="text-center">
                  <svg className={`mx-auto h-8 w-8 mb-2 ${
                    normalizeFile ? 'text-green-500' : 'text-gray-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {normalizeFile ? normalizeFile.name : 'Normalize dosyasını seçin (opsiyonel)'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Excel formatında (.xlsx, .xls)
                  </p>
                </div>
              </label>
            </div>
            {errors.normalize && (
              <p className="mt-1 text-sm text-red-500">{errors.normalize}</p>
            )}
          </div>
          */}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors disabled:opacity-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 bg-[#02c2ff] hover:bg-[#0199cc] text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Ekleniyor...
                </>
              ) : (
                'Şirketi Ekle'
              )}
            </button>
          </div>
        </form>
        </>
        )}
      </div>
    </div>
  );
};

export default AddCompanyModal;
